import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, Clock, Phone, CheckCircle,
  User, MessageSquare, Stethoscope
} from 'lucide-react';

export default function SelfSchedulePage() {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [appointmentType, setAppointmentType] = useState('routine');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // Mock provider for demo
  const providerId = 'demo_provider';

  useEffect(() => {
    if (selectedDate) {
      loadAvailableSlots();
    }
  }, [selectedDate]);

  const loadAvailableSlots = async () => {
    setLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await api.get('/scheduling/available-slots', {
        params: { provider_id: providerId, date: dateStr }
      });
      setAvailableSlots(response.data.available_slots || []);
    } catch (error) {
      console.error('Failed to load slots:', error);
      // Default slots for demo
      setAvailableSlots(['09:00', '09:30', '10:00', '10:30', '11:00', '14:00', '14:30', '15:00']);
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async () => {
    if (!phone || !selectedDate || !selectedTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await api.post('/scheduling/self-schedule', {
        patient_phone: phone,
        provider_id: providerId,
        appointment_type: appointmentType,
        preferred_date: selectedDate.toISOString().split('T')[0],
        preferred_time: selectedTime,
        reason: reason
      });
      setConfirmed(true);
      toast.success('Appointment requested successfully!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to schedule appointment';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (confirmed) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <Card className="max-w-md w-full careiq-card text-center">
          <CardContent className="p-8">
            <div className="w-20 h-20 bg-[#166534]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#166534]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#0A2342] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Appointment Requested!
            </h1>
            <p className="text-[#64748B] mb-6">
              You will receive an SMS confirmation at <strong>{phone}</strong> shortly.
            </p>
            <div className="bg-[#F8FAFC] rounded-lg p-4 text-left mb-6">
              <div className="flex items-center gap-2 text-sm text-[#64748B] mb-2">
                <CalendarIcon className="w-4 h-4" />
                {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </div>
              <div className="flex items-center gap-2 text-sm text-[#64748B]">
                <Clock className="w-4 h-4" />
                {selectedTime}
              </div>
            </div>
            <Button 
              className="careiq-btn-primary w-full"
              onClick={() => {
                setConfirmed(false);
                setStep(1);
                setPhone('');
                setSelectedDate(null);
                setSelectedTime('');
                setReason('');
              }}
            >
              Schedule Another Appointment
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12 px-4" data-testid="self-schedule-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="h-12 w-12 rounded-xl bg-[#0E7490] flex items-center justify-center mx-auto mb-4">
            <Stethoscope className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Schedule Your Appointment
          </h1>
          <p className="text-[#64748B] mt-2">
            Book your visit online in just a few steps
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-[#0E7490] text-white' : 'bg-[#E2E8F0] text-[#64748B]'
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-[#0E7490]' : 'bg-[#E2E8F0]'}`} />}
            </div>
          ))}
        </div>

        <Card className="careiq-card">
          <CardContent className="p-6">
            {/* Step 1: Verify Phone */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center">
                  <Phone className="w-12 h-12 text-[#0E7490] mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-[#0A2342]">Verify Your Phone Number</h2>
                  <p className="text-[#64748B] mt-2">Enter the phone number on file with our clinic</p>
                </div>
                <div>
                  <Label className="careiq-label">Phone Number</Label>
                  <Input
                    type="tel"
                    placeholder="(555) 000-0000"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="careiq-input text-center text-lg"
                    data-testid="phone-input"
                  />
                </div>
                <Button 
                  className="careiq-btn-primary w-full"
                  onClick={() => setStep(2)}
                  disabled={!phone}
                  data-testid="next-step-1"
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Step 2: Select Date & Time */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <CalendarIcon className="w-12 h-12 text-[#0E7490] mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-[#0A2342]">Choose Date & Time</h2>
                </div>
                
                <div>
                  <Label className="careiq-label">Appointment Type</Label>
                  <Select value={appointmentType} onValueChange={setAppointmentType}>
                    <SelectTrigger className="careiq-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine Check-up</SelectItem>
                      <SelectItem value="follow_up">Follow-up Visit</SelectItem>
                      <SelectItem value="urgent">Urgent Care</SelectItem>
                      <SelectItem value="telehealth">Telehealth Visit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="careiq-label">Select Date</Label>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                      className="rounded-lg border"
                    />
                  </div>
                  <div>
                    <Label className="careiq-label">Available Times</Label>
                    {selectedDate ? (
                      loading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="w-6 h-6 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : availableSlots.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                          {availableSlots.map((slot) => (
                            <button
                              key={slot}
                              onClick={() => setSelectedTime(slot)}
                              className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                                selectedTime === slot
                                  ? 'bg-[#0E7490] text-white border-[#0E7490]'
                                  : 'bg-white border-[#E2E8F0] hover:border-[#0E7490] text-[#0A2342]'
                              }`}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[#64748B] text-center py-8">No slots available</p>
                      )
                    ) : (
                      <p className="text-[#64748B] text-center py-8">Select a date first</p>
                    )}
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button variant="outline" className="flex-1" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button 
                    className="careiq-btn-primary flex-1"
                    onClick={() => setStep(3)}
                    disabled={!selectedDate || !selectedTime}
                    data-testid="next-step-2"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Confirm */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center">
                  <CheckCircle className="w-12 h-12 text-[#0E7490] mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-[#0A2342]">Confirm Your Appointment</h2>
                </div>

                <div className="bg-[#F8FAFC] rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[#64748B]">Date</span>
                    <span className="font-medium text-[#0A2342]">
                      {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#64748B]">Time</span>
                    <span className="font-medium text-[#0A2342]">{selectedTime}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#64748B]">Type</span>
                    <span className="font-medium text-[#0A2342] capitalize">{appointmentType.replace('_', ' ')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#64748B]">Phone</span>
                    <span className="font-medium text-[#0A2342]">{phone}</span>
                  </div>
                </div>

                <div>
                  <Label className="careiq-label">Reason for Visit (Optional)</Label>
                  <Input
                    placeholder="Brief description of your visit"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="careiq-input"
                  />
                </div>

                <div className="p-4 bg-[#0E7490]/10 rounded-lg text-sm text-[#0E7490]">
                  <MessageSquare className="w-4 h-4 inline-block mr-2" />
                  You will receive an SMS confirmation and reminder before your appointment.
                </div>

                <div className="flex gap-4">
                  <Button variant="outline" className="flex-1" onClick={() => setStep(2)}>
                    Back
                  </Button>
                  <Button 
                    className="careiq-btn-primary flex-1"
                    onClick={handleSchedule}
                    disabled={loading}
                    data-testid="confirm-appointment"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Confirm Appointment'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
